import {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Alert,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import config from "../../config/config";
import { getAuthToken } from "../../utils/token";
import ProductionPlanDetailsDialog from "../seniors/ProductionPlanDetailsDialog";


const ProductionDispatchPlanItemBom = () => {

    const [productRows, setProductRows] = useState([]);
    const [materialRows, setMaterialRows] = useState([]);

    const [planMonth, setPlanMonth] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const [searchText, setSearchText] = useState("");

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedPOrderId, setSelectedPOrderId] = useState(null);
    // =========================================================
    // FETCH DATA
    // =========================================================

   const fetchData = useCallback(async () => {

    try {

        setLoading(true);
        setError("");

        const token = getAuthToken();

        if (!token) {
            setError(
                "Authentication token not found. Please login first."
            );
            return;
        }

        // -------------------------------------------------
        // QUERY PARAMETERS
        // -------------------------------------------------

        const params = new URLSearchParams();

        // comp_code is NOT sent from frontend.
        // Backend gets it from logged-in user.

        if (planMonth) {
            params.append(
                "plan_month",
                planMonth
            );
        }

        if (dateFrom) {
            params.append(
                "production_start_date_from",
                dateFrom
            );
        }

        if (dateTo) {
            params.append(
                "production_start_date_to",
                dateTo
            );
        }

        // -------------------------------------------------
        // API URL
        // -------------------------------------------------

        const queryString = params.toString();

        const url = queryString
            ? `${config.API}/productiondispatchplan/getPrdPlanandMaterial?${queryString}`
            : `${config.API}/productiondispatchplan/getPrdPlanandMaterial`;

        // console.log("API URL:", url);

        // -------------------------------------------------
        // API REQUEST
        // -------------------------------------------------

        const response = await fetch(url, {

            method: "GET",

            headers: {
                Accept: "application/json",

                Authorization:
                    `Bearer ${token}`,
            },

        });

        const result = await response.json();

        // -------------------------------------------------
        // API ERROR
        // -------------------------------------------------

        if (
            !response.ok ||
            !result.success
        ) {
            throw new Error(
                result.message ||
                "Unable to fetch production dispatch plan."
            );
        }

        // -------------------------------------------------
        // RESULT SET 1
        // -------------------------------------------------

        const products =
            result.data?.product_totals || [];

        // -------------------------------------------------
        // RESULT SET 2
        // -------------------------------------------------

        const materials =
            result.data?.material_requirements || [];

        // -------------------------------------------------
        // PRODUCT ROWS
        // -------------------------------------------------

        setProductRows(
            products.map((row, index) => ({
                ...row,

                id: `${row.product_id}-${index}`,

                // PO ID used by Dispatch History /
                // Inspection Entry
                erp_po_id:
                    row.erp_po_id ??
                    row.POrderId ??
                    row.POrderID ??
                    row.pOrderId,
            }))
        );

        // -------------------------------------------------
        // MATERIAL ROWS
        // -------------------------------------------------

        setMaterialRows(
            materials.map((row, index) => ({
                ...row,

                id:
                    `${row.product_id}-${row.material_id}-${index}`,
            }))
        );

    } catch (err) {

        console.error(
            "Production Dispatch Plan BOM Error:",
            err
        );

        setError(
            err.message ||
            "Something went wrong while loading data."
        );

        setProductRows([]);
        setMaterialRows([]);

    } finally {

        setLoading(false);

    }

}, [
    planMonth,
    dateFrom,
    dateTo
]);


// =========================================================
// INITIAL LOAD
// =========================================================

useEffect(() => {

    fetchData();

}, [fetchData]);

    // =========================================================
    // CLEAR FILTERS
    // =========================================================

    const handleClear = () => {
        setPlanMonth("");
        setDateFrom("");
        setDateTo("");
        setSearchText("");
        setTimeout(() => {
            fetchData();
        }, 0);

    };


    // =========================================================
    // PRODUCT SEARCH
    // =========================================================

    const filteredProductRows = useMemo(() => {

        if (!searchText.trim()) {

            return productRows;

        }


        const search =
            searchText
                .trim()
                .toLowerCase();


        return productRows.filter((row) => {

            return (

                String(row.item || "")
                    .toLowerCase()
                    .includes(search)

                ||

                String(row.product_code || "")
                    .toLowerCase()
                    .includes(search)

            );

        });

    }, [
        productRows,
        searchText
    ]);


    // =========================================================
    // MATERIAL SEARCH
    // =========================================================

    const filteredMaterialRows = useMemo(() => {

        if (!searchText.trim()) {

            return materialRows;

        }


        const search =
            searchText
                .trim()
                .toLowerCase();


        return materialRows.filter((row) => {

            return (

                String(row.item || "")
                    .toLowerCase()
                    .includes(search)

                ||

                String(row.material_name || "")
                    .toLowerCase()
                    .includes(search)

                ||

                String(row.material_code || "")
                    .toLowerCase()
                    .includes(search)

            );

        });

    }, [
        materialRows,
        searchText
    ]);
    // 3. Grouped material rows
    const groupedMaterialRows = useMemo(() => {

        const groups = {};

        filteredMaterialRows.forEach((row) => {

            const key = row.product_id || row.item;

            if (!groups[key]) {

                groups[key] = {
                    product_id: row.product_id,
                    item: row.item,
                    pending_qty: Number(row.pending_qty || 0),
                    totalRequired: 0,
                    totalMaterials: 0,
                    rows: [],
                };
            }

            groups[key].totalRequired += Number(
                row.material_required || 0
            );

            groups[key].totalMaterials += 1;

            groups[key].rows.push(row);

        });

        const result = [];

        Object.values(groups).forEach((group) => {

            result.push({
                id: `group-${group.product_id}`,
                rowType: "group",
                item: group.item,
                pending_qty: group.pending_qty,
                totalRequired: group.totalRequired,
                totalMaterials: group.totalMaterials,
            });

            group.rows.forEach((row) => {

                result.push({
                    ...row,
                    rowType: "material",
                });

            });

        });

        return result;

    }, [filteredMaterialRows]);

const handleProductRowClick = (params) => {
    const pOrderId =
        params.row.erp_po_id ??
        params.row.POrderId ??
        params.row.POrderID ??
        params.row.pOrderId;

    console.log("Product Row:", params.row);
    console.log("Selected POrderId:", pOrderId);

    if (pOrderId == null) {
        console.error(
            "erp_po_id / POrderId not found:",
            params.row
        );
        return;
    }

    setSelectedPOrderId(pOrderId);
    setDetailsOpen(true);
};
    // =========================================================
    // PRODUCT COLUMNS
    // =========================================================

    const productColumns = [
        {
            field: "item",
            headerName: "Item",
            flex: 1,
            minWidth: 120,
        },
        {
            field: "total_order_qty",
            headerName: "Order Qty",
            width: 100,
            type: "number",
            valueFormatter: (value) =>
                Number(value || 0).toFixed(2),
        },
        {
            field: "total_dispatch_qty",
            headerName: "Dispatch Qty",
            width: 100,
            type: "number",

            valueFormatter: (value) =>
                Number(value || 0).toFixed(2),
        },
        {
            field: "pending_qty",
            headerName: "Pending Qty",
            width: 100,
            type: "number",

            valueFormatter: (value) =>
                Number(value || 0).toFixed(2),
        },

        //{
           // field: "available_quantity",
          //  headerName: "In Stock",
          //  width: 100,
          ///  type: "number",

           // valueFormatter: (value) =>
             //   Number(value || 0).toFixed(2),
       // },

        {
            field: "total_po",
            headerName: "PO",
            width: 50,
            type: "number",
        },

        {
            field: "plan_month",
            headerName: "Plan Month",
            width: 110,
        },
        
    ];
    // =========================================================
    // MATERIAL COLUMNS
    // =========================================================
    const materialColumns = [

        {
            field: "item",
            headerName: "Item / Material",
            flex: 1,
            minWidth: 350,

            renderCell: (params) => {

                if (params.row.rowType === "group") {

                    return (

                        <Box
                            sx={{
                                py: 0.5,
                                width: "100%",
                            }}
                        >

                            <Typography
                                sx={{
                                    fontWeight: "bold",
                                    color: "#0d47a1",
                                    fontSize: "14px",
                                }}
                            >
                                {params.row.item}
                            </Typography>

                            <Typography
                                variant="caption"
                                sx={{
                                    color: "#444",
                                }}
                            >
                                Pending Qty :
                                {" "}
                                {Number(
                                    params.row.pending_qty || 0
                                ).toFixed(2)}

                                {" | "}

                                Materials :
                                {" "}
                                {params.row.totalMaterials}

                                {" | "}

                                Total Material Required :
                                {" "}
                                {Number(
                                    params.row.totalRequired || 0
                                ).toFixed(2)}
                            </Typography>

                        </Box>

                    );
                }

                return (

                    <Box
                        sx={{
                            pl: 3,
                        }}
                    >
                        {params.row.material_name}
                    </Box>

                );
            },
        },

        {
            field: "material_per_product",
            headerName: "Used/Pcs",
            width: 100,
            type: "number",

            renderCell: (params) =>

                params.row.rowType === "group"
                    ? ""
                    : Number(
                        params.value || 0
                    ).toFixed(2),
        },

        {
            field: "material_required",
            headerName: "Required",
            width: 120,
            type: "number",

            renderCell: (params) =>

                params.row.rowType === "group"
                    ? ""
                    : Number(
                        params.value || 0
                    ).toFixed(2),
        },

        {
            field: "unit_of_measurement",
            headerName: "UOM",
            width: 80,

            renderCell: (params) =>

                params.row.rowType === "group"
                    ? ""
                    : params.value,
        },

    ];

    // =========================================================
    // COMMON DATAGRID STYLE
    // =========================================================

    const gridSx = {

        border: 0,

        "& .MuiDataGrid-columnHeaders": {

            backgroundColor: "#f5f5f5ff",

            borderBottom:
                "1px solid #ddd",

        },

        "& .MuiDataGrid-columnHeaderTitle": {

            fontWeight: "bold",

        },

        "& .MuiDataGrid-cell": {

            fontSize: "13px",

        },

        "& .MuiDataGrid-row:hover": {

            backgroundColor: "#b8f5f7ff",

        },

    };


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <Box
            sx={{
                p: 1,
                width: "100%",
            }}
        >

            {/* ================================================= */}
            {/* TITLE */}
            {/* ================================================= */}

           


            {/* ================================================= */}
            {/* FILTERS */}
            {/* ================================================= */}

            <Paper
                elevation={2}
                sx={{
                    p: 1,
                    mb: 1,
                }}
            >

                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        alignItems: "center",
                    }}
                >

                    <TextField
                        label="Plan Month"
                        value={planMonth}
                        onChange={(e) =>
                            setPlanMonth(
                                e.target.value
                            )
                        }
                        size="small"
                        placeholder="Oct-2026"
                    />


                    <TextField
                        type="date"
                        value={dateFrom}
                        onChange={(e) =>
                            setDateFrom(
                                e.target.value
                            )
                        }
                        size="small"
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />


                    <TextField
                        type="date"
                        value={dateTo}
                        onChange={(e) =>
                            setDateTo(
                                e.target.value
                            )
                        }
                        size="small"
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />


                    <TextField
                        label="Search"
                        value={searchText}
                        onChange={(e) =>
                            setSearchText(
                                e.target.value
                            )
                        }
                        size="small"
                        placeholder="Item / Material / Code"
                    />


                    <Button
                        variant="contained"
                        onClick={fetchData}
                        disabled={loading}
                    >
                        {loading
                            ? "Loading..."
                            : "Search"}
                    </Button>


                    <Button
                        variant="outlined"
                        onClick={handleClear}
                        disabled={loading}
                    >
                        Clear
                    </Button>

                </Box>

            </Paper>


            {/* ================================================= */}
            {/* ERROR */}
            {/* ================================================= */}

            {error && (

                <Alert
                    severity="error"
                    sx={{
                        mb: 1,
                    }}
                >
                    {error}
                </Alert>

            )}


            {/* ================================================= */}
            {/* LEFT + RIGHT SECTION */}
            {/* ================================================= */}

            <Box
                sx={{
                    display: "grid",

                    gridTemplateColumns: {
                        xs: "1fr",
                        md: "1fr 1fr",
                    },

                    gap: 1,

                    width: "100%",
                }}
            >

                {/* ============================================= */}
                {/* LEFT - PRODUCTION PLAN */}
                {/* ============================================= */}

                <Paper
                    elevation={2}
                    sx={{
                        p: 0.5,
                        minWidth: 0,
                    }}
                >

                   

                    <Box
                        sx={{
                            width: "100%",
                            minWidth: 0,
                            height: 550,
                        }}
                    >
                      <DataGrid
    rows={filteredProductRows}
    columns={productColumns}
    loading={loading}
    disableRowSelectionOnClick
    onRowClick={handleProductRowClick}
    getRowClassName={() => "clickable-product-row"}
    density="compact"
    pageSizeOptions={[10, 25, 50]}
    initialState={{
        pagination: {
            paginationModel: {
                pageSize: 25,
                page: 0,
            },
        },
    }}
    sx={{
        ...gridSx,

        "& .clickable-product-row": {
            cursor: "pointer",
        },

        "& .clickable-product-row:hover": {
            backgroundColor: "#e3f2fd",
        },
    }}
/>
                    </Box>

                </Paper>


                {/* ============================================= */}
                {/* RIGHT - MATERIAL REQUIREMENT */}
                {/* ============================================= */}

                <Paper
                    elevation={2}
                    sx={{
                        p: 0.5,
                        minWidth: 0,
                    }}
                >

                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: "bold", color: "red",
                            px: 1,
                            py: 0.5,
                            fontSize: "16px",
                        }}
                    >
                        Material Requirement
                    </Typography>


                    <Box
                        sx={{
                            width: "100%",
                            minWidth: 0,
                            height: 550,
                        }}
                    >

                        {/* =========================================================
                            INSPECTION ENTRY DIALOG
                        ========================================================= */}

                        <DataGrid
                            rows={groupedMaterialRows}
                            columns={materialColumns}
                            loading={loading}
                            disableRowSelectionOnClick
                            density="compact"
                            hideFooterPagination
                            hideFooterSelectedRowCount
                            getRowClassName={(params) =>
                                params.row.rowType === "group"
                                    ? "material-group-row"
                                    : "material-detail-row"
                            }
                            sx={{
                                ...gridSx,

                                "& .material-group-row": {
                                    backgroundColor: "#e3f2fd",
                                    borderTop: "2px solid #90caf9",
                                    borderBottom: "1px solid #90caf9",
                                    minHeight: "60px !important",
                                },

                                "& .material-group-row:hover": {
                                    backgroundColor: "#bbdefb",
                                },

                                "& .material-detail-row": {
                                    backgroundColor: "#fff",
                                },
                            }}
                        />
                    </Box>

                </Paper>

            </Box>
  {/* Your complete existing UI */}

        <ProductionPlanDetailsDialog
            open={detailsOpen}
            poId={selectedPOrderId}
            onClose={() => {
                setDetailsOpen(false);
                setSelectedPOrderId(null);
            }}
        />
        </Box>
    );
};


export default ProductionDispatchPlanItemBom;
